import { StackLayout } from '@euler/app/components/StackLayout';
import { StatusColors } from '@euler/components/Colors';
import { MaybeText } from '@euler/components/MaybeText';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { createContext, memo, ReactNode, useContext } from 'react';
import { StyleProp, TextStyle, View, ViewStyle } from 'react-native';

type ErrorContentType = Error | ReactNode;

export type FormGroupConfig = {
  style?: StyleProp<ViewStyle>;
  labelStyle?: StyleProp<TextStyle>;
  labelContainerStyle?: StyleProp<ViewStyle>;
  helpTextStyle?: StyleProp<TextStyle>;
  errorTextStyle?: StyleProp<TextStyle>;
};

export const FormGroupContext = createContext<FormGroupConfig | undefined>(
  undefined,
);

const ErrorLabel = memo(
  ({
    error,
    textStyle,
  }: {
    error?: ErrorContentType;
    textStyle?: StyleProp<TextStyle>;
  }) => {
    const { rpx } = useResponsiveContext();
    if (!error) return null;
    return (
      <View
        css={`
          margin-top: ${rpx(8)}px;
        `}
      >
        <MaybeText
          text={error instanceof Error ? error.message : error}
          style={textStyle}
          css={`
            color: ${StatusColors.Danger};
            margin-left: ${rpx(10)}px;
            font-family: ${FontFamily.NotoSans.Thin};
            font-size: ${rpx(12)}px;
          `}
        />
      </View>
    );
  },
);

export const FormGroup = memo(
  ({
    label,
    labelRight,
    helpText,
    error,
    style,
    labelStyle,
    labelContainerStyle,
    helpTextStyle,
    errorTextStyle,
    children,
  }: FormGroupConfig & {
    label?: ReactNode;
    labelRight?: ReactNode;
    helpText?: ReactNode;
    error?: ErrorContentType;
    children?: ReactNode;
  }) => {
    const { rpx } = useResponsiveContext();

    const config = useContext(FormGroupContext);

    return (
      <View
        css={`
          margin-bottom: ${rpx(10)}px;
          padding: ${rpx(10)}px 0;
        `}
        style={[config?.style, style]}
      >
        {label || labelRight ? (
          <StackLayout
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            style={[config?.labelContainerStyle, labelContainerStyle]}
            css={`
              margin-bottom: ${rpx(4)}px;
            `}
          >
            <MaybeText text={label} style={[config?.labelStyle, labelStyle]} />
            {labelRight}
          </StackLayout>
        ) : null}

        {children}
        <MaybeText
          text={helpText}
          style={[config?.helpTextStyle, helpTextStyle]}
        />
        <ErrorLabel
          error={error}
          textStyle={[config?.errorTextStyle, errorTextStyle]}
        />
      </View>
    );
  },
);
